// src/services/AudioService.js
import audioConfig from '@/config/audioConfig';

class AudioService {
  constructor() {
    this.config = audioConfig;
    this.audioElements = {};
    this.audioContext = null;
    this.gainNode = null;
    this.audioUnlocked = false;
    this.backgroundAudio = null;
    this.queue = [];
    this.isPlayingQueue = false;
    this.masterVolume = 1.0;

    this.init();
  }

  /**
   * 初始化音效系统
   */
  init() {
    this.preloadAudio();
    this.setupUnlockHandler();
  }

  /**
   * 预加载配置中的所有音效
   */
  preloadAudio() {
    Object.entries(this.config).forEach(([category, sounds]) => {
      Object.entries(sounds).forEach(([name, sound]) => {
        if (sound.preload) {
          const audio = new Audio(sound.file);
          audio.load();
          audio.dataset.originalVolume = sound.volume;
          this.audioElements[`${category}.${name}`] = audio;
        }
      });
    });
  }

  /**
   * 设置音频解锁处理器
   */
  setupUnlockHandler() {
    const unlock = () => {
      if (this.audioUnlocked) return;

      // 尝试Web Audio API
      try {
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
        this.gainNode = this.audioContext.createGain();
        this.gainNode.gain.value = this.masterVolume;
        this.gainNode.connect(this.audioContext.destination);
        this.audioUnlocked = true;
        return;
      } catch (e) {
        console.warn('Web Audio API初始化失败，回退到HTML5 Audio:', e);
      }

      // HTML5 Audio解锁
      const silentAudio = new Audio('data:audio/wav;base64,UklGRl9vT19XQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YU...');
      silentAudio.volume = 0;
      silentAudio.play()
        .then(() => {
          this.audioUnlocked = true;
        })
        .catch(e => console.error('音频解锁失败:', e));
    };

    // 移动端需要用户交互
    if (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
      document.addEventListener('touchstart', unlock, { once: true });
      document.addEventListener('click', unlock, { once: true });
    } else {
      unlock();
    }
  }

  /**
   * 播放音效
   * @param {string} name 音效名称 (格式: 'category.name' 或 'name')
   * @param {object} options 播放选项
   * @returns {Promise} 播放完成后的Promise
   */
  play(name, options = {}) {
    return new Promise((resolve, reject) => {
      if (!this.audioUnlocked) {
        this.showPermissionTip();
        return reject(new Error('AUDIO_NOT_UNLOCKED'));
      }

      // 查找音效配置
      const soundConfig = this.findSoundConfig(name);
      if (!soundConfig) {
        return reject(new Error(`SOUND_NOT_FOUND: ${name}`));
      }

      // 合并配置
      const playOptions = {
        ...soundConfig,
        ...options,
        volume: (options.volume || soundConfig.volume) * this.masterVolume
      };

      // 优先使用Web Audio API (背景音乐除外)
      if (this.audioContext && !playOptions.loop) {
        this.playWithWebAudio(playOptions)
          .then(resolve)
          .catch(e => {
            console.warn('Web Audio播放失败，尝试HTML5 Audio:', e);
            this.playWithHtml5(playOptions).then(resolve).catch(reject);
          });
      } else {
        this.playWithHtml5(playOptions).then(resolve).catch(reject);
      }
    });
  }

  /**
   * 查找音效配置
   * @private
   */
  findSoundConfig(name) {
    // 直接匹配 'category.name'
    if (name.includes('.')) {
      const [category, soundName] = name.split('.');
      return this.config[category] && this.config[category][soundName];
    }

    // 全局搜索匹配name的音效
    for (const category of Object.values(this.config)) {
      if (category[name]) {
        return category[name];
      }
    }

    return null;
  }

  /**
   * 使用Web Audio API播放
   * @private
   */
  async playWithWebAudio(options) {
    if (!this.audioContext) {
      throw new Error('WEB_AUDIO_NOT_INITIALIZED');
    }

    const response = await fetch(options.file);
    const arrayBuffer = await response.arrayBuffer();
    const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);

    const source = this.audioContext.createBufferSource();
    source.buffer = audioBuffer;

    const gainNode = this.audioContext.createGain();
    gainNode.gain.value = options.volume;

    source.connect(gainNode);
    gainNode.connect(this.audioContext.destination);
    source.start(0);

    return new Promise((resolve) => {
      source.onended = () => resolve();
    });
  }

  /**
   * 使用HTML5 Audio播放
   * @private
   */
  playWithHtml5(options) {
    return new Promise((resolve, reject) => {
      // 直接使用配置中的路径
      const audio = new Audio(options.file);

      audio.volume = options.volume;
      audio.loop = options.loop || false;

      audio.play()
        .then(() => {
          if (options.loop) {
            this.backgroundAudio = audio;
          }

          if (!options.loop) {
            audio.onended = () => resolve();
          } else {
            resolve();
          }
        })
        .catch(e => {
          console.error('HTML5音效播放失败:', e);
          reject(e);
        });
    });
  }

  /**
   * 添加到播放队列
   */
  enqueue(name, options = {}) {
    return new Promise((resolve) => {
      this.queue.push({ name, options, resolve });
      if (!this.isPlayingQueue) {
        this.processQueue();
      }
    });
  }

  /**
   * 处理播放队列
   * @private
   */
  async processQueue() {
    if (this.queue.length === 0) {
      this.isPlayingQueue = false;
      return;
    }

    this.isPlayingQueue = true;
    const { name, options, resolve } = this.queue.shift();

    try {
      await this.play(name, options);
      resolve();
    } catch (e) {
      console.error('队列播放失败:', e);
    }

    this.processQueue();
  }

  /**
   * 停止背景音乐
   */
  stopBackground() {
    if (this.backgroundAudio) {
      this.backgroundAudio.pause();
      this.backgroundAudio = null;
    }
  }

  /**
   * 设置主音量
   * @param {number} volume 0-1之间的音量值
   */
  setMasterVolume(volume) {
    this.masterVolume = Math.min(1, Math.max(0, volume));

    // 更新Web Audio
    if (this.gainNode) {
      this.gainNode.gain.value = this.masterVolume;
    }

    // 更新正在播放的HTML5音频
    if (this.backgroundAudio) {
      const originalVolume = this.backgroundAudio.dataset.originalVolume || 1;
      this.backgroundAudio.volume = this.masterVolume * originalVolume;
    }
  }

  /**
   * 显示音频权限提示
   */
  showPermissionTip() {
    console.warn('请点击页面任意位置解锁音频播放权限');
    // 实际项目中可以显示UI提示
  }

  /**
   * 淡出效果
   */
  fadeOut(duration = 1000) {
    if (!this.backgroundAudio) return Promise.resolve();

    const startVolume = this.backgroundAudio.volume;
    const startTime = performance.now();

    return new Promise((resolve) => {
      const fade = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        this.backgroundAudio.volume = startVolume * (1 - progress);

        if (progress < 1) {
          requestAnimationFrame(fade);
        } else {
          this.stopBackground();
          resolve();
        }
      };

      requestAnimationFrame(fade);
    });
  }

  /**
   * 淡入效果
   */
  fadeIn(name, duration = 1000, options = {}) {
    return new Promise((resolve, reject) => {
      const soundConfig = this.findSoundConfig(name);
      if (!soundConfig) {
        return reject(new Error(`SOUND_NOT_FOUND: ${name}`));
      }

      const targetVolume = (options.volume || soundConfig.volume) * this.masterVolume;
      options.volume = 0;

      this.play(name, options)
        .then(() => {
          const startTime = performance.now();

          const fade = (currentTime) => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);
            this.backgroundAudio.volume = targetVolume * progress;

            if (progress < 1) {
              requestAnimationFrame(fade);
            } else {
              resolve();
            }
          };

          requestAnimationFrame(fade);
        })
        .catch(reject);
    });
  }
}

// 导出单例实例
export default new AudioService();



// WEBPACK FOOTER //
// ./src/services/AudioService.js